/**
 * useParentChrome.ts - Detect whether the embedding page provides its own chrome.
 *
 * When our dashboard embeds the chat iframe it renders a breadcrumb above it
 * (which carries the user avatar + "Go to BOOK CAIR"). In that case the iframe
 * header should stay slim. The dashboard signals this by appending `?embedded=1`
 * to the iframe src. When the iframe is embedded elsewhere (no breadcrumb), the
 * flag is absent and the header shows the avatar + "Go to BOOK CAIR" itself.
 *
 * The flag lives in the hash route (`#/aichat/<id>?embedded=1`), but the app's
 * own navigation (e.g. redirecting to `/new`) drops the query string. So we read
 * it ONCE at module load — before the router redirects — and persist it in
 * sessionStorage so it survives later navigations and in-iframe reloads.
 */

const STORAGE_KEY = 'cair_embedded';

const detectFromHash = (): boolean => {
    const hash = window.location.hash || '';
    const qIndex = hash.indexOf('?');
    if (qIndex === -1) return false;
    return new URLSearchParams(hash.slice(qIndex + 1)).get('embedded') === '1';
};

let embedded = detectFromHash();
try {
    if (embedded) {
        sessionStorage.setItem(STORAGE_KEY, '1');
    } else if (sessionStorage.getItem(STORAGE_KEY) === '1') {
        embedded = true;
    }
} catch {
    // sessionStorage may be unavailable (e.g. blocked in a cross-site iframe);
    // fall back to the value parsed from the hash.
}

export const useParentChrome = (): boolean => embedded;
